import React, { useCallback, useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal, Spinner } from "react-bootstrap";

import { deleteUser, fetchMyPermissions, fetchUsers, fetchUserById, restoreUser, toggleUserStatus } from "../../../redux/slices/userSlice";
import { fetchDelegations } from "../../../redux/slices/delegationSlice";
import { fetchTeams } from "../../../redux/slices/teamSlice";

import { sortAlphabetically } from "../../../utils/sortAlphabetically";
import EmployeeTable from "../../tables/EmployeeTable/EmployeeTable";
import PeopleToolbar from "../OrganizationToolbar";
import CreateEmployeeModal from "../../Modal/EmployeeModal/CreateEmployeeModal/CreateEmployeeModal";
import UserDetailsModal from "../../userComponents/UserDetailsModal/UserDetailsModal";

const getErrorMessage = (error, fallback) => {
  const detail = error?.payload?.detail || error?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item?.loc) ? item.loc[item.loc.length - 1] : null;
        return field ? `${field}: ${item.msg || item.message || ""}` : item.msg || item.message || "";
      })
      .filter(Boolean)
      .join(" | ");
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const PeopleTab = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { items: users = [], isLoading: isUsersLoading } = useSelector((state) => state.users);
    const { items: delegations = [] } = useSelector((state) => state.delegations || { items: [] });
    const { items: teams = [] } = useSelector((state) => state.teams || { items: [] });
    const permissions = useSelector((state) => state.users?.permissions || {});
    const canManageUsers = permissions.can_delete_users === true;
    const canViewSensitive = permissions.can_view_sensitive_users === true;

    const [search, setSearch] = useState("");
    const [filters, setFilters] = useState({
        delegations: [],
        teams: [],
        startDate: "",
        deletedOnly: false,
    });

    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const [showCreateModal, setShowCreateModal] = useState(false);

    const [quickViewUser, setQuickViewUser] = useState(null);
    const [showQuickView, setShowQuickView] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [isConfirming, setIsConfirming] = useState(false);
    const [actionError, setActionError] = useState("");

    useEffect(() => {
        dispatch(fetchMyPermissions());
    }, [dispatch]);

    // Deleted users are only listed for admins, so they can restore them.
    const includeDeleted = canManageUsers && canViewSensitive;

    const reloadUsers = useCallback(() => {
        dispatch(fetchUsers({ includeInactive: canManageUsers, includeDeleted }));
    }, [dispatch, canManageUsers, includeDeleted]);

    useEffect(() => {
        reloadUsers();
        dispatch(fetchDelegations());
        dispatch(fetchTeams());
    }, [dispatch, reloadUsers]);

    const getDelegationName = useCallback((user) => {
        if (user.delegation?.name) return user.delegation.name;
        const delegation = delegations.find(d => String(d.id) === String(user.delegation_id));
        return delegation ? delegation.name : "N/A";
    }, [delegations]);

    const timeAgo = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("pt-PT");
    };

    const filteredUsers = useMemo(() => {
        // Deleted users only show up with the "deleted only" filter, so they
        // don't clutter the regular list.
        let result = users.filter((user) => Boolean(user.deleted_at) === Boolean(filters.deletedOnly));

        if (search) {
            const s = search.toLowerCase();
            result = result.filter(
                (user) =>
                    (user.first_name && user.first_name.toLowerCase().includes(s)) ||
                    (user.last_name && user.last_name.toLowerCase().includes(s)) ||
                    (user.username && user.username.toLowerCase().includes(s)) ||
                    (user.email && user.email.toLowerCase().includes(s))
            );
        }

        if (filters.delegations && filters.delegations.length > 0) {
            result = result.filter((user) => {
                const delegationName = getDelegationName(user);
                return filters.delegations.includes(delegationName);
            });
        }

        if (filters.teams && filters.teams.length > 0) {
            result = result.filter((user) => {
                return user.team_tag && filters.teams.includes(user.team_tag);
            });
        }

        if (filters.startDate) {
            const filterDate = new Date(filters.startDate).getTime();
            result = result.filter((user) => {
                const dateString = user.hiring_date || user.created_at;

                if (!dateString) return true;

                const userStartDate = new Date(dateString).getTime();
                return userStartDate >= filterDate;
            });
        }

        return sortAlphabetically(result, (user) => [user.first_name, user.last_name], i18n.language);
    }, [users, search, filters, getDelegationName, i18n.language]);

    const paginatedUsers = filteredUsers;

    const toggleSelectRow = (id) => {
        const newSelected = new Set(selectedRowIds);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.add(id);
        setSelectedRowIds(newSelected);
    };

    const selectableUsers = paginatedUsers.filter(user => !user.deleted_at);
    const allVisibleSelected = selectableUsers.length > 0 && selectableUsers.every(user => selectedRowIds.has(user.id));

    const toggleSelectAllVisible = () => {
        const newSelected = new Set(selectedRowIds);
        if (allVisibleSelected) {
            paginatedUsers.forEach(user => newSelected.delete(user.id));
        } else {
            paginatedUsers.filter(user => !user.deleted_at).forEach(user => newSelected.add(user.id));
        }
        setSelectedRowIds(newSelected);
    };

    const handleOpenUserPage = (user) => {
        navigate(`/organization/employee/${user.id}`);
    };

    const handleViewUser = async (user) => {
        try {
            const fullUser = await dispatch(fetchUserById(user.id)).unwrap();
            setQuickViewUser(fullUser);
            setShowQuickView(true);
        } catch (error) {
            setActionError(getErrorMessage(error, t('components.peopleTab.loadError', 'Não foi possível carregar o utilizador.')));
        }
    };

    const requestDeleteUser = (user) => {
        if (!canManageUsers) return;
        setActionError("");
        setPendingAction({ type: 'delete', user });
    };

    const requestRestoreUser = (user) => {
        if (!canManageUsers) return;
        setActionError("");
        setPendingAction({ type: 'restore', user });
    };

    const requestToggleStatus = (user) => {
        if (!canManageUsers) return;
        setActionError("");
        setPendingAction({ type: 'status', user });
    };

    const closeConfirmation = () => {
        if (isConfirming) return;
        setPendingAction(null);
        setActionError("");
    };

    const confirmPendingAction = async () => {
        if (!pendingAction) return;

        const { type, user } = pendingAction;
        setIsConfirming(true);
        setActionError("");

        try {
            if (type === 'delete') {
                await dispatch(deleteUser(user.id)).unwrap();
                if (includeDeleted) reloadUsers();
            } else if (type === 'restore') {
                await dispatch(restoreUser(user.id)).unwrap();
                reloadUsers();
            } else {
                await dispatch(
                    toggleUserStatus({ userId: user.id, status: !user.is_active })
                ).unwrap();
            }
            setPendingAction(null);
        } catch (error) {
            // Keep the modal open so the admin can read what went wrong.
            setActionError(
                getErrorMessage(
                    error,
                    t('components.peopleTab.actionError', 'A operação não foi concluída.')
                )
            );
        } finally {
            setIsConfirming(false);
        }
    };

    const pendingUserLabel = pendingAction
        ? pendingAction.user.username || pendingAction.user.email
        : '';
    const isPendingDelete = pendingAction?.type === 'delete';
    const isPendingRestore = pendingAction?.type === 'restore';

    // Only block the tab on the first load, so reloads after an action don't flash.
    if (isUsersLoading && users.length === 0) {
        return <div className="p-4 text-center txt-subtle">A carregar colaboradores...</div>;
    }

    return (
        <div className="people-tab-container">

            <PeopleToolbar
                search={search}
                setSearch={setSearch}
                filters={filters}
                setFilters={setFilters}
                delegations={delegations}
                teams={teams}
                onAddPerson={canManageUsers ? () => setShowCreateModal(true) : undefined}
                canManageUsers={canManageUsers}
                canViewDeleted={includeDeleted}
            />

            <div className="atlar-card overflow-hidden mt-3">
                <EmployeeTable
                    users={paginatedUsers}
                    selectedRowIds={selectedRowIds}
                    allVisibleSelected={allVisibleSelected}
                    toggleSelectAllVisible={toggleSelectAllVisible}
                    toggleSelectRow={toggleSelectRow}
                    getDelegationName={getDelegationName}
                    timeAgo={timeAgo}
                    onOpenUserPage={handleOpenUserPage}
                    onViewUser={handleViewUser}
                    onDeleteUser={canManageUsers ? requestDeleteUser : undefined}
                    onToggleStatus={canManageUsers ? requestToggleStatus : undefined}
                    onRestoreUser={canManageUsers ? requestRestoreUser : undefined}
                    canManageUsers={canManageUsers}
                    canViewSensitive={canViewSensitive}
                />
            </div>

            {canManageUsers && (
                <CreateEmployeeModal
                    show={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onUserCreated={() => {
                        setShowCreateModal(false);
                        reloadUsers();
                    }}
                />
            )}

            <UserDetailsModal
                user={quickViewUser}
                show={showQuickView}
                handleClose={() => setShowQuickView(false)}
                delegations={delegations}
                startInEditMode={false}
            />

            <Modal show={Boolean(pendingAction)} onHide={closeConfirmation} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold" style={{ fontSize: '1.1rem' }}>
                        {isPendingDelete
                            ? t('components.peopleTab.confirmDeleteTitle', 'Eliminar utilizador')
                            : isPendingRestore
                                ? t('components.peopleTab.confirmRestoreTitle', 'Restaurar utilizador')
                                : t('components.peopleTab.confirmStatusTitle', 'Alterar estado da conta')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        {isPendingDelete
                            ? t('components.peopleTab.confirmDeleteBody', {
                                defaultValue: 'Tem a certeza de que quer eliminar {{name}}? A conta deixará de ter acesso ao sistema.',
                                name: pendingUserLabel,
                            })
                            : isPendingRestore
                            ? t('components.peopleTab.confirmRestoreBody', {
                                defaultValue: 'Tem a certeza de que quer restaurar {{name}}? A conta volta a ficar ativa e com acesso ao sistema.',
                                name: pendingUserLabel,
                            })
                            : t('components.peopleTab.confirmStatusBody', {
                                defaultValue: 'Tem a certeza de que quer {{action}} a conta de {{name}}?',
                                action: pendingAction?.user?.is_active
                                    ? t('components.peopleTab.deactivate', 'desativar')
                                    : t('components.peopleTab.activate', 'ativar'),
                                name: pendingUserLabel,
                            })}
                    </p>
                    {actionError && (
                        <div className="alert alert-danger mt-3 mb-0" role="alert">
                            {actionError}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={closeConfirmation}
                        disabled={isConfirming}
                    >
                        {t('common.cancel', 'Cancelar')}
                    </Button>
                    <Button
                        variant={isPendingDelete ? 'danger' : 'primary'}
                        onClick={confirmPendingAction}
                        disabled={isConfirming}
                    >
                        {isConfirming && (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                        )}
                        {isPendingDelete
                            ? t('common.delete', 'Eliminar')
                            : isPendingRestore
                                ? t('components.peopleTab.restore', 'Restaurar')
                                : t('common.confirm', 'Confirmar')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default PeopleTab;